import { createAsyncThunk } from "@reduxjs/toolkit";
import axios, { AxiosResponse } from "axios";
import { baseUrlApi } from "../../proxy";
import dayjs from "dayjs";
import CAxios from "../utils/CAxios";

interface NeighborhoodsInterface {
  id: number;
}

interface OTPProps {
  otp: string;
  request_id?: number;
  model: "beneficiary" | "sponsor" | "login";
  mobile?: string;
  email?: string;
  whatsapp?: string;
}

// to Add Beneficiary
export const toAddBeneficiary = createAsyncThunk<AxiosResponse<any, any>, any>(
  "recipient/toAddBeneficiary",
  async (beneficiary) => {
    const config = { headers: { "Content-Type": "application/json" } };
    return await axios.post(`${baseUrlApi}beneficiary`, beneficiary, config);
  }
);

// to update Beneficiary
export const toUpdateBeneficiary = createAsyncThunk<
  AxiosResponse<any, any>,
  any
>("recipient/toUpdateBeneficiary", async (beneficiary) => {
  const config = { headers: { "Content-Type": "application/json" } };
  return await axios.post(
    `${baseUrlApi}beneficiary/update/information`,
    beneficiary,
    config
  );
});

// to family members
export const toAddFamilyMembers = createAsyncThunk<
  AxiosResponse<any, any>,
  any
>("recipient/toAddFamilyMembers", async (orphans) => {
  const config = { headers: { "Content-Type": "application/json" } };
  return await axios.post(`${baseUrlApi}beneficiary/orphans`, orphans, config);
});

// to add housing informations
export const toAddHousingInformations = createAsyncThunk<
  AxiosResponse<any, any>,
  any
>("recipient/toAddHousingInformations", async (housing) => {
  const config = { headers: { "Content-Type": "application/json" } };
  return await axios.post(`${baseUrlApi}beneficiary/housing`, housing, config);
});

// to add source incomes
export const toAddSourceIncomes = createAsyncThunk<
  AxiosResponse<any, any>,
  any
>("recipient/toAddSourceIncomes", async (incomes) => {
  const config = { headers: { "Content-Type": "application/json" } };
  return await axios.post(
    `${baseUrlApi}beneficiary/material/states`,
    incomes,
    config
  );
});

// to add proof documents
export const toAddProofDocuments = createAsyncThunk<
  AxiosResponse<any, any>,
  any
>("recipient/toAddProofDocuments", async (docs) => {
  const config = { headers: { "Content-Type": "application/json" } };
  return await axios.post(
    `${baseUrlApi}beneficiary/proof/documents`,
    docs,
    config
  );
});

export const toVerifyOtp = createAsyncThunk<AxiosResponse<any, any>, OTPProps>(
  "recipient/toVerifyOtp",
  async ({ otp, request_id, model, mobile, email, whatsapp }: OTPProps) => {
    const config = { headers: { "Content-Type": "application/json" } };
    let endpoint = "";
    let data = {};

    switch (model) {
      case "beneficiary":
        endpoint = `${baseUrlApi}verify/otp`;
        data = { otp, beneficiary_request_id: request_id };
        break;
      case "sponsor":
        endpoint = `${baseUrlApi}sponsor/verify/otp`;
        data = { otp, sponsor_request_id: request_id };
        break;
      case "login":
        endpoint = `${baseUrlApi}login/verify/otp`;
        data = { login: email || whatsapp || mobile, otp };
        break;
      default:
        break;
    }

    return await axios.post(endpoint, data, config);
  }
);

// to regenerate otp
export const toRegenerateOtp = createAsyncThunk<AxiosResponse<any, any>, any>(
  "recipient/toRegenerateOtp",
  async ({ id, res_model, mobile, whatsapp }) => {
    const config = { headers: { "Content-Type": "application/json" } };
    return await axios.post(
      `${baseUrlApi}regenerate/otp`,
      whatsapp ? { id, res_model, whatsapp } : { id, res_model, mobile },
      config
    );
  }
);

// to print declaration
export const toPrintDeclaration = createAsyncThunk<
  AxiosResponse<any, any>,
  any
>("recipient/toPrintDeclaration", async (beneficiary_request_id) => {
  const config = {
    responseType: "blob" as const,
    headers: {
      "Content-Type": "application/json",
    },
  };
  const { data } = await axios.get(
    `${baseUrlApi}beneficiary/declaration/print/${beneficiary_request_id}`,
    config
  );
  // Create a Blob URL for the PDF
  const pdfBlob = new Blob([data], { type: "application/pdf" });
  const fileURL = URL.createObjectURL(pdfBlob);
  const link = document.createElement("a");
  link.href = fileURL;
  link.download = dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss") + ".pdf";
  link.click();

  return data;
});

// get all Nationality
export const getAllNationality = createAsyncThunk<AxiosResponse<any, any>>(
  "recipient/getAllNationality",
  async () => {
    try {
      let response = await axios.get(`${baseUrlApi}countries`);
      return response;
    } catch (error) {
      throw error;
    }
  }
);

// get all governorates
export const getAllGovernorates = createAsyncThunk<AxiosResponse<any, any>>(
  "recipient/getAllGovernorates",
  async () => {
    try {
      let response = await axios.get(`${baseUrlApi}beneficiary/governorates`);
      return response;
    } catch (error) {
      throw error;
    }
  }
);

// get all neighborhood
export const getAllNeighborhood = createAsyncThunk<
  AxiosResponse<any, any>,
  NeighborhoodsInterface,
  { rejectValue: string }
>("recipient/getAllNeighborhood", async (id: NeighborhoodsInterface) => {
  try {
    let response = await axios.post(`${baseUrlApi}beneficiary/neighborhoods`, {
      governorate_id: id,
    });
    return response;
  } catch (error) {
    throw error;
  }
});

// get all neighborhood
export const getAllNeighborhoodOnly = createAsyncThunk<AxiosResponse<any, any>>(
  "recipient/getAllNeighborhoodOnly",
  async () => {
    try {
      let response = await axios.post(
        `${baseUrlApi}beneficiary/neighborhoods`,
        {}
      );
      return response;
    } catch (error) {
      throw error;
    }
  }
);

// get all death reasons
export const getAllDeathReasons = createAsyncThunk<AxiosResponse<any, any>>(
  "recipient/getAllDeathReasons",
  async () => {
    try {
      let response = await axios.get(`${baseUrlApi}beneficiary/death/reasons`);
      return response;
    } catch (error) {
      throw error;
    }
  }
);

// get all marital status
export const getAllMaritalStatus = createAsyncThunk<AxiosResponse<any, any>>(
  "recipient/getAllMaritalStatus",
  async () => {
    try {
      let response = await axios.get(`${baseUrlApi}beneficiary/marital/status`);
      return response;
    } catch (error) {
      throw error;
    }
  }
);

// get all academic levels
export const getAllAcademicLevels = createAsyncThunk<AxiosResponse<any, any>>(
  "recipient/getAllAcademicLevels",
  async () => {
    try {
      let response = await axios.get(
        `${baseUrlApi}beneficiary/academic/levels`
      );
      return response;
    } catch (error) {
      throw error;
    }
  }
);

// get all health states
export const getAllHealthStates = createAsyncThunk<AxiosResponse<any, any>>(
  "recipient/getAllHealthStates",
  async () => {
    try {
      let response = await axios.get(`${baseUrlApi}beneficiary/health/states`);
      return response;
    } catch (error) {
      throw error;
    }
  }
);

// get all identification types
export const getAllIdentificationTypes = createAsyncThunk<
  AxiosResponse<any, any>
>("recipient/getAllIdentificationTypes", async () => {
  try {
    let response = await axios.get(
      `${baseUrlApi}beneficiary/identification/types`
    );
    return response;
  } catch (error) {
    throw error;
  }
});

// get all housing types
export const getAllHousingTypes = createAsyncThunk<AxiosResponse<any, any>>(
  "recipient/getAllHousingTypes",
  async () => {
    try {
      let response = await axios.get(`${baseUrlApi}beneficiary/housing/types`);
      return response;
    } catch (error) {
      throw error;
    }
  }
);

// get all housing states
export const getAllHousingStates = createAsyncThunk<AxiosResponse<any, any>>(
  "recipient/getAllHousingStates",
  async () => {
    try {
      let response = await axios.get(`${baseUrlApi}beneficiary/housing/states`);
      return response;
    } catch (error) {
      throw error;
    }
  }
);

// get all housing ownerships
export const getAllHousingOwnerships = createAsyncThunk<
  AxiosResponse<any, any>
>("recipient/getAllHousingOwnerships", async () => {
  try {
    let response = await axios.get(
      `${baseUrlApi}beneficiary/housing/ownership`
    );
    return response;
  } catch (error) {
    throw error;
  }
});

// get all income sources
export const getAllIncomeSources = createAsyncThunk<AxiosResponse<any, any>>(
  "recipient/getAllIncomeSources",
  async () => {
    try {
      let response = await axios.get(`${baseUrlApi}beneficiary/income/sources`);
      return response;
    } catch (error) {
      throw error;
    }
  }
);

// get all disease type
export const getAllDiseaseType = createAsyncThunk<AxiosResponse<any, any>>(
  "recipient/getAllDiseaseType",
  async () => {
    try {
      let response = await axios.get(`${baseUrlApi}beneficiary/disease/types`);
      return response;
    } catch (error) {
      throw error;
    }
  }
);

// get academics years
export const getAllAcademicsYears = createAsyncThunk<AxiosResponse<any, any>>(
  "recipient/getAllAcademicsYears",
  async () => {
    try {
      let response = await axios.get(`${baseUrlApi}beneficiary/academic/years`);
      return response;
    } catch (error) {
      throw error;
    }
  }
);

// get academics performance
export const getAllAcademicsPerformance = createAsyncThunk<
  AxiosResponse<any, any>
>("recipient/getAllAcademicsPerformance", async () => {
  try {
    let response = await axios.get(
      `${baseUrlApi}beneficiary/academic/performance`
    );
    return response;
  } catch (error) {
    throw error;
  }
});

// get educational institution types
export const getAllEducationalInstitutionTypes = createAsyncThunk<
  AxiosResponse<any, any>
>("recipient/getAllEducationalInstitutionTypes", async () => {
  try {
    let response = await axios.get(
      `${baseUrlApi}beneficiary/educational/institution/types`
    );
    return response;
  } catch (error) {
    throw error;
  }
});

// get beneficiary info
export const getAllOrphansBeneficiaryInfo = createAsyncThunk<
  AxiosResponse<any, any>,
  any
>("recipient/getAllOrphansBeneficiaryInfo", async (access_token) => {
  try {
    let response = await axios.get(`${baseUrlApi}beneficiary/${access_token}`);
    return response;
  } catch (error) {
    throw error;
  }
});
//GET Beneficiary details
export const getBeneficiaryDetails = createAsyncThunk<AxiosResponse<any, any>>(
  "recipient/getBeneficiaryDetails",
  async () => {
    try {
      let response = await CAxios.get(`beneficiary/details`);
      return response?.data;
    } catch (error) {
      throw error;
    }
  }
);

// get guardian relationships
export const getAllGuardianRelationships = createAsyncThunk<
  AxiosResponse<any, any>
>("recipient/getAllGuardianRelationships", async () => {
  try {
    let response = await axios.get(
      `${baseUrlApi}beneficiary/guardian/relationships`
    );
    return response;
  } catch (error) {
    throw error;
  }
});

// get disability types
export const getAllDisabilityTypes = createAsyncThunk<AxiosResponse<any, any>>(
  "recipient/getAllDisabilityTypes",
  async () => {
    try {
      let response = await axios.get(
        `${baseUrlApi}beneficiary/disability/types`
      );
      return response;
    } catch (error) {
      throw error;
    }
  }
);

/***************************** Update beneficiary info ************************************** */

// to update mother bank info
export const toUpdateMotherBankInfo = createAsyncThunk<
  AxiosResponse<any, any>,
  any
>("recipient/toUpdateMotherBankInfo", async (beneficiary) => {
  const config = { headers: { "Content-Type": "application/json" } };
  return await axios.post(
    `${baseUrlApi}beneficiary/bank/account/update`,
    beneficiary,
    config
  );
});

// to update orphans infos
export const toUpdateOrphansInfo = createAsyncThunk<
  AxiosResponse<any, any>,
  any
>("recipient/toUpdateOrphansInfo", async (beneficiary) => {
  const config = { headers: { "Content-Type": "application/json" } };
  return await axios.post(
    `${baseUrlApi}beneficiary/orphans/update`,
    beneficiary,
    config
  );
});

// to update guardian infos
export const toUpdateGuardianInfo = createAsyncThunk<
  AxiosResponse<any, any>,
  any
>("recipient/toUpdateGuardianInfo", async (beneficiary) => {
  const config = { headers: { "Content-Type": "application/json" } };
  return await axios.post(
    `${baseUrlApi}beneficiary/guardian/update`,
    beneficiary,
    config
  );
});

// to update housing infos
export const toUpdateHousingInfo = createAsyncThunk<
  AxiosResponse<any, any>,
  any
>("recipient/toUpdateHousingInfo", async (beneficiary) => {
  const config = { headers: { "Content-Type": "application/json" } };
  return await axios.post(
    `${baseUrlApi}beneficiary/housing/update`,
    beneficiary,
    config
  );
});

// to update assistances infos
export const toUpdateAssistancesInfo = createAsyncThunk<
  AxiosResponse<any, any>,
  any
>("recipient/toUpdateAssistancesInfo", async (beneficiary) => {
  const config = { headers: { "Content-Type": "application/json" } };
  return await axios.post(
    `${baseUrlApi}beneficiary/assistances/update`,
    beneficiary,
    config
  );
});

// to update documents infos
export const toUpdateDocumentsInfo = createAsyncThunk<
  AxiosResponse<any, any>,
  any
>("recipient/toUpdateDocumentsInfo", async (beneficiary) => {
  const config = { headers: { "Content-Type": "application/json" } };
  return await axios.post(
    `${baseUrlApi}beneficiary/family/records/update`,
    beneficiary,
    config
  );
});
//to  get all proof documents
export const getProofDocuments = createAsyncThunk<AxiosResponse<any, any>>(
  "recipient/getProofDocuments",
  async () => {
    try {
      let response = await CAxios.get(`family/proof/documents/details`);
      return response?.data;
    } catch (error) {
      throw error;
    }
  }
);
// to add proof document
export const addProofDocument = createAsyncThunk<AxiosResponse<any, any>, any>(
  "services/addProofDocument",
  async (proofDocumentData: any) => {
    try {
      const response = await CAxios.post(
        `family/proof/documents`,
        proofDocumentData
      );
      return response;
    } catch (error) {
      throw error;
    }
  }
);
// to get bank accounts
export const getAllBankAccounts = createAsyncThunk<AxiosResponse<any, any>>(
  "recipient/getAllBankAccounts",
  async () => {
    try {
      let response = await CAxios.get(
        `${baseUrlApi}family/bank/accounts/details`
      );
      return response;
    } catch (error) {
      throw error;
    }
  }
);
// to add bank account
export const addBankAccount = createAsyncThunk<AxiosResponse<any, any>, any>(
  "services/addBankAccount",
  async (bankAccountData: any) => {
    try {
      const response = await CAxios.post(
        `family/bank/accounts`,
        bankAccountData
      );
      return response;
    } catch (error) {
      throw error;
    }
  }
);
// to edit beneficiary persons
export const toEditBeneficiaryPersons = createAsyncThunk<
  AxiosResponse<any, any>,
  any
>("beneficiary/persons/edit", async (EditInfos) => {
  try {
    const response = await CAxios.post(`beneficiary/persons/edit`, EditInfos);
    return response;
  } catch (error) {
    throw error;
  }
});

// to get donation amount
export const getDonationAmount = createAsyncThunk<
  any,
  {
    limit?: number;
    page?: number;
    date_from?: string;
    date_to?: string;
  }
>("recipient/family/donation/amount", async (data) => {
  try {
    const response = await CAxios.post(
      `${baseUrlApi}family/donation/amount`,
      data
    );
    return response?.data?.result;
  } catch (error) {
    throw error;
  }
});

// to get ddonation product
export const getDonationProduct = createAsyncThunk<
  any,
  {
    limit?: number;
    page?: number;
    date_from?: string;
    date_to?: string;
  }
>("recipient/family/donation/product", async (data) => {
  try {
    const response = await CAxios.post(
      `${baseUrlApi}family/donation/product`,
      data
    );
    return response?.data?.result;
  } catch (error) {
    throw error;
  }
});

// to edit beneficiary persons
export const toEditIncomeSource = createAsyncThunk<
  AxiosResponse<any, any>,
  any
>("beneficiary/material/states/edit", async (EditInfos) => {
  try {
    const response = await CAxios.post(
      `beneficiary/material/states/edit`,
      EditInfos
    );
    return response;
  } catch (error) {
    throw error;
  }
});
// to edit beneficiary housing  types
export const toEditBeneficiaryHousing = createAsyncThunk<
  AxiosResponse<any, any>,
  any
>("beneficiary/housing/types/edit", async (EditInfos) => {
  try {
    const response = await CAxios.post(
      `beneficiary/housing/types/edit`,
      EditInfos
    );
    return response;
  } catch (error) {
    throw error;
  }
});
// to edit beneficiary housing infos
export const toEditHousingInfos = createAsyncThunk<
  AxiosResponse<any, any>,
  any
>("beneficiary/housing/edit", async (EditInfos) => {
  try {
    const response = await CAxios.post(`beneficiary/housing/edit`, EditInfos);
    return response;
  } catch (error) {
    throw error;
  }
});
// to edit beneficiary guardian infos
export const toEditGuardianInfos = createAsyncThunk<
  AxiosResponse<any, any>,
  any
>("beneficiary/guardian/edit", async (EditInfos) => {
  try {
    const response = await CAxios.post(`beneficiary/guardian/edit`, EditInfos);
    return response;
  } catch (error) {
    throw error;
  }
});
// create contact
export const addcontactUsRequest = createAsyncThunk<
  AxiosResponse<any, any>,
  any
>("contactus/addRequest", async (contactUsData: any) => {
  try {
    const response = await CAxios.post(`user/helpdesk/tickets`, contactUsData);
    return response;
  } catch (error) {
    throw error;
  }
});
// Contact us list
export const getContactUsList = createAsyncThunk<
  any,
  { limit?: number; page?: number }
>("ContactUs/getContactUsList", async (data) => {
  try {
    const response = await CAxios.post(`user/helpdesk/tickets/details`, data);
    return response?.data?.result;
  } catch (error) {
    throw error;
  }
});


// get all liability types
export const getAllLiabilityType = createAsyncThunk<AxiosResponse<any, any>>(
  "recipient/getAllLiabilityType",
  async () => {
    try {
      let response = await axios.get(`${baseUrlApi}beneficiary/liability/types`);
      return response;
    } catch (error) {
      throw error;
    }
  }
);


// to edit beneficiary persons
export const toEditLiabilities = createAsyncThunk<
  AxiosResponse<any, any>,
  any
>("beneficiary/liabilities/edit", async (EditInfos) => {
  try {
    const response = await CAxios.post(
      `beneficiary/liabilities/edit`,
      EditInfos
    );
    return response;
  } catch (error) {
    throw error;
  }
});
// to add liabitities
export const toAddLiabitities= createAsyncThunk<
  AxiosResponse<any, any>,
  any
>("recipient/toAddLiabitities", async (liabilities) => {
  const config = { headers: { "Content-Type": "application/json" } };
  return await axios.post(
    `${baseUrlApi}beneficiary/liabilities`,
    liabilities,
    config
  );
});
// to update housing infos
export const toUpdateLiabilityInfos = createAsyncThunk<
  AxiosResponse<any, any>,
  any
>("recipient/toUpdateLiabilityInfos", async (liabilities) => {
  const config = { headers: { "Content-Type": "application/json" } };
  return await axios.post(
    `${baseUrlApi}beneficiary/liabilities/update`,
    liabilities,
    config
  );
});
